import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import type { InputRef } from 'antd';
import { Button, Flex, Input, Typography } from 'antd';

import filesActions from '../../../actions';

import AiIcon from '../../../../../components/AiIcon';

import { message } from '../../../../../utils/message';

import { AppDispatch } from '../../../../../types/app';

const { Text } = Typography;

type Props = {
  fileId: number;
  name: string;
  justify?: React.ComponentProps<typeof Flex>['justify'];
};

const FileEditableName = ({ fileId, name, justify }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<InputRef>(null);
  const savingRef = useRef(false);
  const shouldSubmitOnBlurRef = useRef(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  useEffect(() => {
    setDraftName(name);
  }, [name]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const startEditing = useCallback(() => {
    shouldSubmitOnBlurRef.current = true;
    setDraftName(name);
    setIsEditing(true);
  }, [name]);

  const cancelEditing = useCallback(() => {
    shouldSubmitOnBlurRef.current = false;
    setDraftName(name);
    setIsEditing(false);
  }, [name]);

  const submitRename = useCallback(async () => {
    const trimmedName = draftName.trim();

    if (!trimmedName || trimmedName.length > 255) {
      cancelEditing();
      return;
    }

    if (trimmedName === name) {
      setIsEditing(false);
      return;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      await dispatch(filesActions.Files.renameFile(fileId, trimmedName));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      message.error('files.rename.error');
      setDraftName(name);
      setIsEditing(false);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [cancelEditing, dispatch, draftName, fileId, name]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void submitRename();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancelEditing();
      }
    },
    [cancelEditing, submitRename]
  );

  const handleBlur = useCallback(() => {
    window.setTimeout(() => {
      if (!shouldSubmitOnBlurRef.current) {
        shouldSubmitOnBlurRef.current = true;
        return;
      }

      if (!savingRef.current) {
        void submitRename();
      }
    }, 0);
  }, [submitRename]);

  return (
    <Flex
      align="center"
      justify={justify}
      gap={8}
      flex={1}
      style={{ minWidth: 0 }}
    >
      {isEditing ? (
        <Input
          ref={inputRef}
          className="files-page__card-title-input"
          value={draftName}
          maxLength={255}
          disabled={saving}
          onChange={(event) => setDraftName(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-label={intl.formatMessage({
            id: 'files.card.renameInput',
          })}
        />
      ) : (
        <Text className="files-page__card-title" ellipsis>
          {name}
        </Text>
      )}
      <Button
        type="text"
        className="files-page__card-edit"
        icon={<AiIcon name="edit" />}
        disabled={isEditing}
        onClick={startEditing}
        aria-label={intl.formatMessage({
          id: 'files.card.renameInput',
        })}
      />
    </Flex>
  );
};

export default FileEditableName;
